/**
 * Client Universe data layer (sidekick surface).
 *
 * Talks to /sidekick/wiki, which resolves the client from the row for every
 * route except the two entry points that name it. Access is "assigned to this
 * client AND the client is still active", enforced server-side.
 *
 * Two things here are load-bearing rather than incidental:
 *
 *  * **content_version round-trips on every save.** A page is a whole-document
 *    PUT and both the client and their PA can edit it, so a stale write is
 *    refused with 409 instead of silently discarding the other person's work.
 *    Callers must surface that, not swallow it — see isConflict().
 *
 *  * **Page staleTime sits under the image presign expiry.** Image URLs are
 *    short-lived and arrive alongside the document; caching a page for longer
 *    than they live would render broken images.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiRequest } from "@/lib/queryClient";
import type {
  WikiContentUpdate,
  WikiImage,
  WikiImageUploadUrl,
  WikiPage,
  WikiSection,
  WikiTree,
} from "@shared/wiki";

const BASE = "/sidekick/wiki";

/** Presigned image URLs last an hour; stay comfortably inside that. */
const DOC_STALE_MS = 30 * 60 * 1000;

export const wikiTreeKey = (clientUid: string) => ["wiki-tree", clientUid] as const;
export const wikiSectionKey = (sectionId: number) => ["wiki-section", sectionId] as const;
export const wikiPageKey = (pageId: number) => ["wiki-page", pageId] as const;

/** True when a save lost the optimistic-lock race and must be reloaded. */
export function isConflict(error: unknown): boolean {
  return error instanceof Error && error.message.startsWith("409");
}

async function json<T>(res: Response): Promise<T> {
  return (await res.json()) as T;
}

// --- Reads --------------------------------------------------------------------
export function useWikiTree(clientUid: string | undefined) {
  return useQuery<WikiTree>({
    queryKey: wikiTreeKey(clientUid ?? ""),
    enabled: !!clientUid,
    queryFn: async () => json(await apiRequest("GET", `${BASE}/clients/${clientUid}/tree`)),
  });
}

export function useWikiSection(sectionId: number | undefined) {
  return useQuery<WikiSection>({
    queryKey: wikiSectionKey(sectionId ?? 0),
    enabled: !!sectionId,
    staleTime: DOC_STALE_MS,
    queryFn: async () => json(await apiRequest("GET", `${BASE}/sections/${sectionId}`)),
  });
}

export function useWikiPage(pageId: number | undefined) {
  return useQuery<WikiPage>({
    queryKey: wikiPageKey(pageId ?? 0),
    enabled: !!pageId,
    staleTime: DOC_STALE_MS,
    queryFn: async () => json(await apiRequest("GET", `${BASE}/pages/${pageId}`)),
  });
}

// --- Sections -----------------------------------------------------------------
export function useCreateSection(clientUid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { title: string; icon?: string | null }) =>
      json<WikiSection>(
        await apiRequest("POST", `${BASE}/clients/${clientUid}/sections`, payload),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: wikiTreeKey(clientUid) }),
  });
}

export function useUpdateSection(clientUid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: WikiContentUpdate & { id: number }) =>
      json<WikiSection>(await apiRequest("PATCH", `${BASE}/sections/${id}`, payload)),
    onSuccess: (section) => {
      qc.setQueryData(wikiSectionKey(section.id), section);
      qc.invalidateQueries({ queryKey: wikiTreeKey(clientUid) });
    },
  });
}

export function useDeleteSection(clientUid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sectionId: number) => {
      await apiRequest("DELETE", `${BASE}/sections/${sectionId}`);
      return sectionId;
    },
    onSuccess: (sectionId) => {
      qc.removeQueries({ queryKey: wikiSectionKey(sectionId) });
      qc.invalidateQueries({ queryKey: wikiTreeKey(clientUid) });
    },
  });
}

// --- Pages --------------------------------------------------------------------
export function useCreatePage(clientUid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ sectionId, title }: { sectionId: number; title: string }) =>
      json<WikiPage>(
        await apiRequest("POST", `${BASE}/sections/${sectionId}/pages`, { title }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: wikiTreeKey(clientUid) }),
  });
}

export function useUpdatePage(clientUid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: WikiContentUpdate & { id: number }) =>
      json<WikiPage>(await apiRequest("PATCH", `${BASE}/pages/${id}`, payload)),
    onSuccess: (page) => {
      qc.setQueryData(wikiPageKey(page.id), page);
      // Title and position both show in the nav, so the tree is stale either way.
      qc.invalidateQueries({ queryKey: wikiTreeKey(clientUid) });
    },
  });
}

export function useDeletePage(clientUid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pageId: number) => {
      await apiRequest("DELETE", `${BASE}/pages/${pageId}`);
      return pageId;
    },
    onSuccess: (pageId) => {
      qc.removeQueries({ queryKey: wikiPageKey(pageId) });
      qc.invalidateQueries({ queryKey: wikiTreeKey(clientUid) });
    },
  });
}

// --- Images -------------------------------------------------------------------
export const WIKI_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
export const WIKI_IMAGE_ACCEPT = WIKI_IMAGE_TYPES.join(",");
export const WIKI_IMAGE_MAX_BYTES = 10 * 1024 * 1024;

export function validateWikiImage(file: File): string | null {
  if (!WIKI_IMAGE_TYPES.includes(file.type)) {
    return "Images must be PNG, JPEG, WebP or GIF.";
  }
  if (file.size > WIKI_IMAGE_MAX_BYTES) {
    return `"${file.name}" is larger than 10 MB.`;
  }
  return null;
}

/**
 * Presign → direct browser PUT → HEAD-verified confirm, mirroring the task
 * attachment flow. The PUT goes straight to S3 and deliberately carries no
 * Authorization header — the presigned URL is the credential, and adding one
 * would break the signature.
 */
export async function uploadWikiImage(
  sectionId: number,
  file: File,
  pageId: number | null,
): Promise<WikiImage> {
  const presign = await json<WikiImageUploadUrl>(
    await apiRequest("POST", `${BASE}/sections/${sectionId}/images/upload-url`, {
      filename: file.name,
      content_type: file.type,
      page_id: pageId,
    }),
  );

  const put = await fetch(presign.upload_url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  if (!put.ok) throw new Error(`Upload failed (${put.status})`);

  return json<WikiImage>(
    await apiRequest("POST", `${BASE}/sections/${sectionId}/images`, {
      object_key: presign.object_key,
      filename: file.name,
      content_type: file.type,
      page_id: pageId,
    }),
  );
}
